import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FormControl, FormLabel, useTheme } from '@mui/material';
import {
  ButtonTertiary,
  GlassButton,
  GlassColors,
  GlassTextField,
} from '../glassmorphism/GlassmorphismStyles';

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
}

export default function ForgotPassword({
  open,
  handleClose,
}: ForgotPasswordProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          handleClose();
        },
      }}
      sx={{
        backdropFilter: 'blur(4px)',
        zIndex: 1900,
        '& .MuiDialog-container': {
          alignItems: 'start',
        },
        '& .MuiPaper-root': {
          borderRadius: '32px',
          m: { xs: '22px', tablet: '32px' },
          border: 'none',
          background: isDark
            ? GlassColors.dark.primaryBackground
            : GlassColors.light.primaryBackground,
          boxShadow: `
            -2.82px -2.82px 8.49px 0px #FFFFFF4D,
            14.15px 14.15px 22.64px -5.37px #0000001A,
            -7.54px -7.54px 8.66px 0px #FFFFFF33,
            5px 5px 24px -11.7px #00000033;
          `,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: { xs: '20px', tablet: '24px' },
          filter: 'drop-shadow(0px -0.25px 0.4px #0000008C)',
          fontWeight: { xs: 800, tablet: 700 },
          lineHeight: { xs: '160%', tablet: '133%' },
          letterSpacing: { xs: '.15px', tablet: 0 },
          color: isDark ? GlassColors.dark.accent : GlassColors.light.secondary,
          p: { xs: '24px 24px 8px 24px', tablet: '32px 32px 8px 32px' },
        }}
      >
        Reset password
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          p: { xs: '0 24px 16px', tablet: '0 32px 16px' },
        }}
      >
        <DialogContentText
          sx={{
            mb: '16px',
            fontFamily: 'roboto, sans-serif',
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: { xs: '143%', sm: '150%' },
            letterSpacing: '.15px',
            color: isDark
              ? GlassColors.dark.textSecondary
              : GlassColors.light.textSecondary,
          }}
        >
          Enter your account&apos;s email address, and we&apos;ll send you a
          link to reset your password.
        </DialogContentText>

        <FormControl fullWidth>
          <FormLabel
            htmlFor='firstname'
            sx={{
              color: isDark
                ? GlassColors.dark.textSecondary
                : GlassColors.textPrimary,
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            Email*
          </FormLabel>
          <GlassTextField
            autoFocus
            required
            id='email'
            name='email'
            placeholder=''
            type='email'
            fullWidth
          />
        </FormControl>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: '0 24px 24px 24px', tablet: '0 32px 32px 32px' },
          justifyContent: 'space-between',
        }}
      >
        <ButtonTertiary
          onClick={handleClose}
          sx={{
            '&&': {
              fontSize: { xs: '12px', tablet: '14px' },
              lineHeight: { xs: '24px', tablet: '29px' },
              fontWeight: 500,
              p: { xs: '4px 24px', tablet: '5px 27px' },
              width: { xs: '91px', tablet: '120px' },
            },
          }}
        >
          Cancel
        </ButtonTertiary>
        <GlassButton
          variant='contained'
          type='submit'
          sx={{
            '&&': {
              fontSize: { xs: '12px', tablet: '14px' },
              lineHeight: { xs: '24px', tablet: '29px' },
              fontWeight: 500,
              p: { xs: '4px 24px', tablet: '5px 27px' },
              width: { xs: '91px', tablet: '120px' },
            },
          }}
        >
          Continue
        </GlassButton>
      </DialogActions>
    </Dialog>
  );
}
